import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * A render error anywhere in a page would otherwise blank the whole app with no
 * way back. This keeps the shell usable and offers a route out.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[pokegrove] render error', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="pxframe pxframe--raised panel" style={{ maxWidth: 520, margin: '10vh auto' }}>
        <div className="stack center">
          <div style={{ fontSize: '2.5rem' }} aria-hidden>
            ⚠
          </div>
          <h2>Something broke on this page</h2>
          <p className="tiny muted">
            Your collection is safe — nothing is saved in the browser. Reloading usually fixes it.
          </p>
          {import.meta.env.DEV && (
            <pre
              className="pxwell tiny"
              style={{ padding: 'var(--gap-sm)', textAlign: 'left', overflowX: 'auto', width: '100%' }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div className="row" style={{ justifyContent: 'center' }}>
            <button className="btn" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            <button className="btn btn--primary" onClick={() => window.location.assign('/')}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
